import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import s from './s.module.less';
import { Button, InputNumber, message, Select, Spin } from 'antd';
import { ReminderSettings } from 'types/calendarSetting';
import { updateReminderSettings } from 'api/calendarSetting';

interface AdvanceNoticeProps {
    ignoreValue?: boolean;
    value?: ReminderSettings | null;
    // One-time seed for the `ignoreValue` mode (e.g. inside a Form.Item where the
    // injected numeric value can't be used as the `value` object). Prefills the
    // selected hours once when it first becomes available, without re-syncing on
    // every change (so it won't fight the onChange feedback loop or Custom input).
    initialValue?: number;
    onChange?: (v: any) => void;
    scheduleToken?: string;
}

const AdvanceNotice = forwardRef((props: AdvanceNoticeProps, ref) => {
    const { value, onChange, ignoreValue, initialValue, scheduleToken } = props;
    const seededRef = React.useRef(false);
    const [timeSet, setTimeSet] = React.useState(0);
    const [inputHour, setInputHour] = React.useState<number>();
    const [loading, setLoading] = React.useState(false);

    const showEdit = timeSet === -1;

    useEffect(() => {
        if (showEdit) {
            onChange?.(inputHour);
        } else {
            onChange?.(timeSet);
        }
    }, [timeSet, inputHour, showEdit]);

    React.useEffect(() => {
        if (!ignoreValue) {
            setTimeSet(value?.minAdvanceBookingHours || 0);
        }
    }, [value, ignoreValue]);

    React.useEffect(() => {
        if (ignoreValue && !seededRef.current && typeof initialValue === 'number') {
            seededRef.current = true;
            setTimeSet(initialValue);
        }
    }, [ignoreValue, initialValue]);

    const options = [
        {
            label: 'No restriction',
            value: 0,
        },
        {
            label: '1 hour',
            value: 1,
        },
        {
            label: '2 hour',
            value: 2,
        },
        {
            label: '3 hour',
            value: 3,
        },
        {
            label: '4 hour',
            value: 4,
        },
    ];

    const exist = options.find((item) => {
        return item.value === timeSet;
    });
    if (!exist) {
        options.push({
            label: `${timeSet} hour`,
            value: timeSet,
        });
    }

    options.push({
        label: 'Custom',
        value: -1,
    });

    const onUpdateData = async () => {
        const data = {
            minAdvanceBookingHours: timeSet === -1 ? inputHour : timeSet,
        };
        setLoading(true);
        const result = await updateReminderSettings(data, scheduleToken);
        setLoading(false);
        if (!result.error) {
            message.success('Advance time updated successfully.');
            setInputHour(undefined);
        }
    };

    useImperativeHandle(ref, () => ({
        onUpdate: () => {
            return onUpdateData();
        },
    }));

    return (
        <div>
            {!showEdit && (
                <div>
                    <Spin spinning={loading}>
                        <div className={s.item}>
                            <Select
                                loading={loading}
                                value={timeSet}
                                onChange={(val) => {
                                    setTimeSet(val);
                                }}
                                style={{ width: '250px' }}
                            >
                                {options.map((item) => {
                                    return <Select.Option value={item.value}>{item.label}</Select.Option>;
                                })}
                            </Select>
                        </div>
                    </Spin>
                    {timeSet ? (
                        <div className={s.subTips}>Patients must book at least {timeSet} hours in advance</div>
                    ) : null}
                </div>
            )}
            {showEdit && (
                <div className={s.itemEdit}>
                    <div className={s.inputWrap}>
                        <InputNumber
                            className={s.input}
                            value={inputHour}
                            maxLength={3}
                            onChange={(val) => {
                                if (!val) {
                                    setInputHour(val || undefined);
                                    return;
                                }
                                // 确保只允许整数
                                const intVal = Math.floor(Number(val));
                                if (intVal < 0) {
                                    setInputHour(1);
                                } else if (intVal > 240) {
                                    setInputHour(240);
                                } else {
                                    setInputHour(intVal);
                                }
                            }}
                            controls={false}
                            step={1}
                            min={1}
                            max={240}
                            precision={0}
                        />
                        hours
                    </div>
                    {inputHour ? (
                        <div className={s.subTips}>Patients must book at least {inputHour} hours in advance</div>
                    ) : null}
                </div>
            )}
        </div>
    );
});

export default AdvanceNotice;
